import { DEFAULT_PREFERENCES, resolvedThemeMode, type UserPreferences } from '@base/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from './api.ts';

/**
 * Preferências de leitura de quem está logado (ADR 0128, fase 6): claro, escuro ou do aparelho,
 * letra maior e alto contraste. Ficam no perfil da pessoa (a API) e viram marcas no `<html>`
 * (`data-theme`, `data-text`, `data-contrast`, `data-menu`) que o styles.css e o tema leem.
 */
export const PREFERENCES_KEY = ['me', 'preferences'];

export function usePreferences() {
  return useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: () => api.get<UserPreferences>('/me/preferences'),
    staleTime: Infinity,
  });
}

export function useSavePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: UserPreferences) => api.put<UserPreferences>('/me/preferences', p),
    onMutate: (p) => qc.setQueryData(PREFERENCES_KEY, p),
    onSuccess: (data) => qc.setQueryData(PREFERENCES_KEY, data),
    onError: () => void qc.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });
}

/** O aparelho está no escuro agora (e acompanha a troca). */
function useDeviceDark(): boolean {
  const query = '(prefers-color-scheme: dark)';
  const [dark, setDark] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = (e: MediaQueryListEvent) => setDark(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return dark;
}

/** As marcas do `<html>` para umas preferências (separado para o teste). */
export function readingMarks(p: UserPreferences, deviceDark: boolean): Record<string, string> {
  return {
    theme: resolvedThemeMode(p.themeMode, deviceDark),
    text: p.largeText ? 'large' : 'normal',
    contrast: p.highContrast ? 'more' : 'normal',
    // O menu claro só muda o modo claro; no escuro a lateral segue o tema (ADR 0138).
    menu: p.lightMenu ? 'light' : 'theme',
  };
}

/** Liga as preferências de leitura à página (dentro da moldura logada). Ao sair, volta ao claro. */
export function ReadingPreferences() {
  const prefs = usePreferences().data ?? DEFAULT_PREFERENCES;
  const deviceDark = useDeviceDark();
  useEffect(() => {
    const marks = readingMarks(prefs, deviceDark);
    const root = document.documentElement;
    for (const [k, v] of Object.entries(marks)) root.dataset[k] = v;
    return () => {
      for (const k of Object.keys(marks)) delete root.dataset[k];
    };
  }, [prefs, deviceDark]);
  return null;
}
